import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, linkWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../../lib/firebase';
import CoursedeeNavbar from '../../components/CoursedeeNavbar';
import ReceiptModal from '../../components/ReceiptModal';
import { 
  Package, 
  History, 
  User, 
  Lock, 
  Mail, 
  Phone, 
  Calendar,
  CheckCircle,
  Loader2,
  Chrome,
  LogOut,
  Settings,
  ArrowRight,
  Sparkles,
  CreditCard,
  TrendingUp,
  Clock,
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  Tag,
  Receipt,
  RefreshCw,
  Infinity
} from 'lucide-react';

interface PurchaseHistory {
  id: string;
  planName: string;
  planPrice: number;
  paymentId: string;
  createdAt: Date;
  status: string;
  expiryDate?: Date | null;
  isLifetime?: boolean;
  discountCode?: string;
  originalPrice?: number;
  isRenewal?: boolean;
  items?: any[];
  fullName?: string;
  buyerName?: string;
  source?: 'coursedee_cart' | 'salepage' | 'subscription';
}

interface CoursedeePackage {
  id: string;
  name: string;
  version: string;
  price: number;
  purchaseDate: any;
  paymentId: string;
}

interface AIPackage {
  id: string;
  name: string;
  version: string;
  price: number;
  description: string;
  features: string[];
  coverImage: string;
  active: boolean;
  order: number;
}

const CoursededDashboard: React.FC = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<any>(null);
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistory[]>([]);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [coursedeePackages, setCoursedeePackages] = useState<CoursedeePackage[]>([]);
  const [selectedReceipt, setSelectedReceipt] = useState<PurchaseHistory | null>(null);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [aiPackages, setAiPackages] = useState<AIPackage[]>([]);
  const [activeTab, setActiveTab] = useState<'packages' | 'history' | 'profile'>('packages');
  
  // Password change states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Username edit states
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameSuccess, setUsernameSuccess] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadUserData();
  }, [user, navigate]);

  const loadUserData = async () => {
    if (!user) {
      console.log('[CoursededDashboard] No user logged in');
      return;
    }

    try {
      setLoading(true);
      console.log('[CoursededDashboard] Loading data for user:', user.uid, user.email);

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      let userDataPackages: CoursedeePackage[] = [];
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        console.log('[CoursededDashboard] User document found:', data);
        setUserData(data);
        
        // Get coursedee packages from user data
        if (data.coursedeePackages && Array.isArray(data.coursedeePackages)) {
          console.log('[CoursededDashboard] coursedeePackages field found:', data.coursedeePackages);
          userDataPackages = data.coursedeePackages.map((pkg: any) => ({
            id: pkg.id,
            name: pkg.name,
            version: pkg.version,
            price: pkg.price,
            purchaseDate: pkg.purchaseDate,
            paymentId: pkg.paymentId
          }));
        } else {
          console.log('[CoursededDashboard] No coursedeePackages field in user document');
        }
      } else {
        console.log('[CoursededDashboard] User document NOT found in Firestore');
      }

      // Also get packages from payments collection (Coursedee Cart purchases)
      console.log('[CoursededDashboard] Checking payments collection...');
      const paymentsRef = collection(db, 'payments');
      
      // Query by userId first
      const paymentsQueryByUserId = query(
        paymentsRef,
        where('userId', '==', user.uid),
        where('status', '==', 'verified')
      );
      const paymentsSnapshotByUserId = await getDocs(paymentsQueryByUserId);
      console.log('[CoursededDashboard] Payments by userId:', paymentsSnapshotByUserId.size);
      
      // Also query by email as fallback
      const paymentsQueryByEmail = query(
        paymentsRef,
        where('email', '==', user.email),
        where('status', '==', 'verified')
      );
      const paymentsSnapshotByEmail = await getDocs(paymentsQueryByEmail);
      console.log('[CoursededDashboard] Payments by email:', paymentsSnapshotByEmail.size);
      
      const cartPackages: CoursedeePackage[] = [];
      const processedPaymentIds = new Set<string>();
      
      // Process payments from both queries
      [paymentsSnapshotByUserId, paymentsSnapshotByEmail].forEach((snapshot) => {
        snapshot.forEach((doc) => {
          // Skip if already processed
          if (processedPaymentIds.has(doc.id)) return;
          processedPaymentIds.add(doc.id);
          
          const data = doc.data();
          // Extract packages from cart items
          if (data.items && Array.isArray(data.items)) {
            data.items.forEach((item: any) => {
              cartPackages.push({
                id: item.id || doc.id,
                name: item.name || 'AIForYou Package',
                version: item.version || 'v1',
                price: item.price || 0,
                purchaseDate: data.createdAt || data.paymentDate,
                paymentId: doc.id
              });
            });
          }
        });
      });

      // Merge packages from user data and payments collection
      const allPackages = [...userDataPackages, ...cartPackages];
      console.log('[CoursededDashboard] All packages before dedup:', allPackages.length, allPackages);
      // Remove duplicates based on id
      const uniquePackages = allPackages.filter((pkg, index, self) => 
        index === self.findIndex((p) => p.id === pkg.id)
      );
      console.log('[CoursededDashboard] Unique packages:', uniquePackages.length, uniquePackages);
      setCoursedeePackages(uniquePackages);

      // Get purchase history from subscription_payments
      const subscriptionPaymentsRef = collection(db, 'subscription_payments');
      const subscriptionQuery = query(
        subscriptionPaymentsRef,
        where('userId', '==', user.uid),
        where('status', 'in', ['approved', 'verified'])
      );
      const subscriptionSnapshot = await getDocs(subscriptionQuery);
      console.log('[CoursededDashboard] Subscription payments found:', subscriptionSnapshot.size);
      
      const history: PurchaseHistory[] = [];
      
      // Add subscription payments
      subscriptionSnapshot.forEach((doc) => {
        const data = doc.data();
        history.push({
          id: doc.id,
          planName: data.planName || 'EduMi Pro',
          planPrice: data.amount || 149,
          paymentId: data.paymentId || doc.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          status: data.status || 'approved',
          expiryDate: data.expiryDate?.toDate() || null,
          isLifetime: data.isLifetime || false,
          discountCode: data.discountCode || data.couponCode || null,
          originalPrice: data.originalPrice || data.originalAmount || null,
          isRenewal: data.isRenewal || false,
          fullName: data.fullName || data.buyerName || null,
          buyerName: data.buyerName || data.fullName || null,
          source: 'subscription'
        });
      });

      // Add Coursedee cart payments
      const processedHistoryIds = new Set<string>();
      console.log('[CoursededDashboard] Processing payments - ByUserId:', paymentsSnapshotByUserId.size, 'ByEmail:', paymentsSnapshotByEmail.size);
      
      [paymentsSnapshotByUserId, paymentsSnapshotByEmail].forEach((snapshot) => {
        snapshot.forEach((doc) => {
          // Skip if already processed
          if (processedHistoryIds.has(doc.id)) return;
          processedHistoryIds.add(doc.id);
          
          const data = doc.data();
          console.log('[CoursededDashboard] Payment data:', doc.id, data);
          console.log('[CoursededDashboard] fullName:', data.fullName, 'buyerName:', data.buyerName);
          console.log('[CoursededDashboard] discountCode:', data.discountCode, 'couponCode:', data.couponCode);
          
          // Use finalPrice from items if available, otherwise use totalAmount
          const totalPrice = data.totalAmount !== null && data.totalAmount !== undefined 
            ? data.totalAmount 
            : data.items?.reduce((sum: number, item: any) => {
                const finalPrice = item.finalPrice !== null && item.finalPrice !== undefined ? item.finalPrice : item.price || 0;
                return sum + finalPrice;
              }, 0) || 0;
          
          const originalPrice = data.originalAmount !== null && data.originalAmount !== undefined
            ? data.originalAmount
            : data.items?.reduce((sum: number, item: any) => sum + (item.originalPrice || item.price || 0), 0) || null;
          
          const purchaseItem = {
            id: doc.id,
            planName: data.items?.map((item: any) => item.name).join(' + ') || 'AIForYou Package',
            planPrice: totalPrice,
            paymentId: doc.id,
            createdAt: data.createdAt?.toDate() || data.paymentDate?.toDate() || new Date(),
            status: data.status || 'verified',
            expiryDate: data.expiryDate?.toDate() || null,
            isLifetime: data.isLifetime !== false, // Coursedee packages are lifetime by default
            discountCode: data.discountCode || data.couponCode || null,
            originalPrice: originalPrice !== totalPrice ? originalPrice : null,
            isRenewal: false,
            items: data.items || [],
            fullName: data.fullName || data.buyerName || null,
            buyerName: data.buyerName || data.fullName || null,
            source: 'coursedee_cart'
          };
          console.log('[CoursededDashboard] Purchase item created:', purchaseItem);
          history.push(purchaseItem);
        });
      });

      // Sort by date (newest first)
      history.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      console.log('[CoursededDashboard] Final purchase history:', history.length, 'items');
      setPurchaseHistory(history);

      // Load AI Packages from Firestore
      console.log('[CoursededDashboard] Loading AI packages...');
      const aiPackagesRef = collection(db, 'aiPackages');
      const aiPackagesSnapshot = await getDocs(aiPackagesRef);
      const aiPackagesData = aiPackagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AIPackage[];
      aiPackagesData.sort((a, b) => a.order - b.order);
      console.log('[CoursededDashboard] AI packages loaded:', aiPackagesData.length, aiPackagesData);
      setAiPackages(aiPackagesData);

    } catch (error) {
      console.error('[CoursededDashboard] Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!newPassword || newPassword.length < 6) {
      setPasswordError('รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('รหัสผ่านใหม่ไม่ตรงกัน');
      return;
    }

    if (!currentPassword) {
      setPasswordError('กรุณากรอกรหัสผ่านปัจจุบัน');
      return;
    }

    setChangingPassword(true);

    try {
      if (!auth.currentUser || !user?.email) {
        throw new Error('ไม่พบข้อมูลผู้ใช้');
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password
      await updatePassword(auth.currentUser, newPassword);

      setPasswordSuccess('✅ เปลี่ยนรหัสผ่านสำเร็จ!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('[CoursededDashboard] Password change error:', error);
      
      if (error.code === 'auth/wrong-password') {
        setPasswordError('รหัสผ่านปัจจุบันไม่ถูกต้อง');
      } else if (error.code === 'auth/too-many-requests') {
        setPasswordError('มีการพยายามมากเกินไป กรุณารอสักครู่');
      } else {
        setPasswordError(error.message || 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleUpdateUsername = async () => {
    setUsernameError('');
    setUsernameSuccess('');

    if (!newUsername || newUsername.trim().length < 3) {
      setUsernameError('Username ต้องมีอย่างน้อย 3 ตัวอักษร');
      return;
    }

    // Validate username format (alphanumeric, dash, underscore)
    const usernameRegex = /^[a-z0-9_-]+$/;
    if (!usernameRegex.test(newUsername.toLowerCase())) {
      setUsernameError('Username ใช้ได้เฉพาะ a-z, 0-9, - และ _ เท่านั้น');
      return;
    }

    setSavingUsername(true);

    try {
      if (!user?.uid) {
        throw new Error('ไม่พบข้อมูลผู้ใช้');
      }

      // Update username in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        username: newUsername.toLowerCase().trim()
      });

      setUsernameSuccess('✅ อัปเดต Username สำเร็จ!');
      setEditingUsername(false);
      setNewUsername('');
      
      // Reload user data
      await loadUserData();
    } catch (error: any) {
      console.error('[CoursededDashboard] Username update error:', error);
      setUsernameError(error.message || 'เกิดข้อผิดพลาดในการอัปเดต Username');
    } finally {
      setSavingUsername(false);
    }
  };

  const handleLinkGoogle = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('ไม่พบข้อมูลผู้ใช้');
      }

      await linkWithPopup(auth.currentUser, googleProvider);
      alert('✅ เชื่อมต่อบัญชี Google สำเร็จ! ตั้งแต่นี้คุณสามารถเข้าสู่ระบบด้วย Google ได้');
      loadUserData();
    } catch (error: any) {
      console.error('[CoursededDashboard] Link Google error:', error);
      
      if (error.code === 'auth/credential-already-in-use') {
        alert('❌ บัญชี Google นี้ถูกใช้งานแล้ว');
      } else if (error.code === 'auth/provider-already-linked') {
        alert('✅ บัญชีของคุณเชื่อมต่อกับ Google อยู่แล้ว');
      } else {
        alert('❌ เกิดข้อผิดพลาดในการเชื่อมต่อบัญชี Google');
      }
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/');
  };

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalPackages = coursedeePackages.length;
  const totalSpent = purchaseHistory.reduce((sum, p) => sum + p.planPrice, 0);
  const latestPackage = coursedeePackages[0];

  return (
    <>
      <CoursedeeNavbar />
      
      {/* Receipt Modal */}
      {selectedReceipt && (
        <ReceiptModal
          isOpen={isReceiptModalOpen}
          onClose={() => {
            setIsReceiptModalOpen(false);
            setSelectedReceipt(null);
          }}
          purchase={selectedReceipt}
          userEmail={userData?.email || user?.email}
          userName={userData?.fullName || userData?.username}
        />
      )}
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-16">
        {/* Header with Gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white">
          <div className="container mx-auto px-4 py-6 md:py-8">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-2xl md:text-4xl font-bold mb-1 md:mb-2">Dashboard</h1>
                <p className="text-purple-100 text-sm md:text-lg truncate">ยินดีต้อนรับ, {userData?.fullName || userData?.username || user?.email}</p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="hidden md:flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-xl font-bold hover:bg-purple-50 transition-all duration-300 transform hover:scale-105 shadow-lg flex-shrink-0"
              >
                <ShoppingBag className="w-5 h-5" />
                ซื้อแพ็คเก็จเพิ่ม
              </button>
              {/* Mobile buy button (icon only) */}
              <button
                onClick={() => navigate('/')}
                className="md:hidden flex items-center justify-center w-11 h-11 bg-white text-purple-600 rounded-xl shadow-lg flex-shrink-0"
                aria-label="ซื้อแพ็คเก็จเพิ่ม"
              >
                <ShoppingBag className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="container mx-auto px-4 -mt-6 md:-mt-8 mb-6 md:mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {/* Total Packages */}
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">แพ็คเก็จทั้งหมด</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900">{totalPackages}</p>
            </div>

            {/* Total Spent */}
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">ยอดใช้จ่ายทั้งหมด</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900">฿{totalSpent.toLocaleString()}</p>
            </div>

            {/* Latest Package */}
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">แพ็คเก็จล่าสุด</p>
              <p className="text-base md:text-lg font-bold text-gray-900 truncate">{latestPackage?.name || '-'}</p>
            </div>

            {/* Member Since */}
            <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 border border-gray-200 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>
              <p className="text-gray-600 text-xs md:text-sm font-medium mb-1">สมาชิกตั้งแต่</p>
              <p className="text-base md:text-lg font-bold text-gray-900">
                {userData?.createdAt?.toDate?.()?.toLocaleDateString('th-TH', { month: 'short', year: 'numeric' }) || '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="container mx-auto px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6">
          <div className="flex gap-2 p-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('packages')}
              className={`flex items-center gap-2 px-3 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all ${
                activeTab === 'packages'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Package className="w-5 h-5" />
              แพ็คเก็จของฉัน
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex items-center gap-2 px-3 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all ${
                activeTab === 'history'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <History className="w-5 h-5" />
              ประวัติการซื้อ
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center gap-2 px-3 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold text-sm md:text-base whitespace-nowrap flex-shrink-0 transition-all ${
                activeTab === 'profile'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-5 h-5" />
              ตั้งค่าโปรไฟล์
            </button>
          </div>
        </div>

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coursedeePackages.length > 0 ? (
              coursedeePackages.map((pkg) => {
                // Map version to correct URL path
                const versionMap: { [key: string]: string } = {
                  'v1': '/v1',
                  'V1': '/v1',
                  'v2': '/v2',
                  'V2': '/v2',
                  'v3': '/v3',
                  'V3': '/v3',
                  'v4': '/v4',
                  'V4': '/v4'
                };
                const targetUrl = versionMap[pkg.version] || '/v1';
                
                // Find matching AI package for cover image
                const aiPackage = aiPackages.find(
                  ai => ai.version.toLowerCase() === pkg.version.toLowerCase()
                );
                
                const coverImage = aiPackage?.coverImage || '';

                return (
                  <div
                    key={pkg.id}
                    onClick={() => {
                      console.log('[CoursededDashboard] Card clicked! Navigating to:', targetUrl, 'for package:', pkg.name, pkg.version);
                      navigate(targetUrl);
                    }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] group cursor-pointer"
                  >
                    {/* Cover Image */}
                    <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-purple-100 to-indigo-100">
                      {coverImage ? (
                        <img
                          src={coverImage}
                          alt={pkg.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Sparkles className="w-16 h-16 text-purple-400" />
                        </div>
                      )}
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-full text-xs font-semibold shadow-lg pointer-events-none">
                        <CheckCircle className="w-3 h-3" />
                        ใช้งานได้
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-4 line-clamp-2 min-h-[3.5rem]">
                        {pkg.name}
                      </h3>
                      
                      {/* Action Button Visual */}
                      <div className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 group-hover:from-purple-700 group-hover:to-indigo-700 text-white rounded-xl font-bold transition-all duration-300 shadow-lg group-hover:shadow-xl">
                        เข้าใช้งาน
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-12">
                <div className="text-center max-w-md mx-auto">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Package className="w-10 h-10 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">ยังไม่มีแพ็คเก็จ</h3>
                  <p className="text-gray-600 mb-6">
                    เริ่มต้นใช้งาน AIForYou เพื่อสร้างคอนเทนต์ที่น่าสนใจ
                  </p>
                  <button
                    onClick={() => navigate('/')}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <ShoppingBag className="w-5 h-5" />
                    ดูแพ็คเก็จทั้งหมด
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {purchaseHistory.length > 0 ? (
              purchaseHistory.map((purchase, index) => {
                const isExpanded = expandedCards.has(purchase.id);
                const gradients = [
                  'from-purple-500 to-indigo-500',
                  'from-blue-500 to-cyan-500',
                  'from-orange-500 to-pink-500',
                  'from-green-500 to-emerald-500',
                ];
                const gradient = gradients[index % gradients.length];
                
                return (
                  <div
                    key={purchase.id}
                    className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    {/* Header Bar */}
                    <div className={`h-2 bg-gradient-to-r ${gradient}`} />
                    
                    {/* Main Content */}
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                            {purchase.isRenewal ? (
                              <RefreshCw className="w-7 h-7 text-white" />
                            ) : (
                              <Receipt className="w-7 h-7 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-bold text-xl text-gray-900 truncate">{purchase.planName}</h4>
                              {purchase.isRenewal && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg flex items-center gap-1">
                                  <RefreshCw className="w-3 h-3" />
                                  ต่ออายุ
                                </span>
                              )}
                            </div>
                            
                            {/* Quick Info */}
                            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  {purchase.createdAt.toLocaleDateString('th-TH', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                              
                              {purchase.isLifetime ? (
                                <div className="flex items-center gap-1 text-purple-600 font-semibold">
                                  <Infinity className="w-4 h-4" />
                                  <span>ตลอดชีพ</span>
                                </div>
                              ) : purchase.expiryDate ? (
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  <span>
                                    หมดอายุ: {purchase.expiryDate.toLocaleDateString('th-TH', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        
                        {/* Price Section */}
                        <div className="text-right ml-4">
                          {purchase.originalPrice && purchase.originalPrice > purchase.planPrice ? (
                            <div>
                              <p className="text-sm text-gray-400 line-through">฿{purchase.originalPrice.toLocaleString()}</p>
                              <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                                ฿{purchase.planPrice.toLocaleString()}
                              </p>
                              <p className="text-xs text-green-600 font-semibold mt-1">
                                ประหยัด ฿{(purchase.originalPrice - purchase.planPrice).toLocaleString()}
                              </p>
                            </div>
                          ) : (
                            <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                              ฿{purchase.planPrice.toLocaleString()}
                            </p>
                          )}
                          <div className="flex items-center gap-1 text-green-600 text-sm mt-2 font-medium justify-end">
                            <CheckCircle className="w-4 h-4" />
                            ชำระเงินแล้ว
                          </div>
                        </div>
                      </div>

                      {/* Expandable Details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                          {/* Payment ID */}
                          <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                            <Receipt className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-500 font-medium mb-1">Payment ID</p>
                              <p className="text-sm text-gray-900 font-mono break-all">{purchase.paymentId}</p>
                            </div>
                          </div>

                          {/* Discount Code */}
                          {purchase.discountCode && (
                            <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                              <Tag className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1">
                                <p className="text-xs text-green-700 font-medium mb-1">โค้ดส่วนลด</p>
                                <p className="text-sm text-green-900 font-bold">{purchase.discountCode}</p>
                              </div>
                            </div>
                          )}

                          {/* Package Items (for Coursedee) */}
                          {purchase.items && purchase.items.length > 0 && (
                            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                              <p className="text-xs text-purple-700 font-medium mb-2 flex items-center gap-1">
                                <Package className="w-4 h-4" />
                                รายการแพ็คเก็จ
                              </p>
                              <div className="space-y-2">
                                {purchase.items.map((item: any, idx: number) => (
                                  <div key={idx} className="flex items-center justify-between text-sm">
                                    <span className="text-purple-900 font-medium">{item.name}</span>
                                    <span className="text-purple-700">฿{item.price?.toLocaleString()}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Purchase Date & Time */}
                          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl">
                            <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs text-blue-700 font-medium mb-1">วันที่ทำรายการ</p>
                              <p className="text-sm text-blue-900 font-semibold">
                                {purchase.createdAt.toLocaleDateString('th-TH', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                          </div>

                          {/* Expiry Info */}
                          <div className={`flex items-start gap-3 p-3 rounded-xl ${
                            purchase.isLifetime 
                              ? 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200' 
                              : 'bg-orange-50 border border-orange-200'
                          }`}>
                            {purchase.isLifetime ? (
                              <>
                                <Infinity className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-xs text-purple-700 font-medium mb-1">ระยะเวลาใช้งาน</p>
                                  <p className="text-sm text-purple-900 font-bold">ตลอดชีพ (ไม่มีวันหมดอายุ)</p>
                                </div>
                              </>
                            ) : purchase.expiryDate ? (
                              <>
                                <Clock className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-xs text-orange-700 font-medium mb-1">วันหมดอายุ</p>
                                  <p className="text-sm text-orange-900 font-semibold">
                                    {purchase.expiryDate.toLocaleDateString('th-TH', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </p>
                                </div>
                              </>
                            ) : (
                              <>
                                <Infinity className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-xs text-purple-700 font-medium mb-1">ระยะเวลาใช้งาน</p>
                                  <p className="text-sm text-purple-900 font-bold">ไม่มีวันหมดอายุ</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => {
                            setSelectedReceipt(purchase);
                            setIsReceiptModalOpen(true);
                          }}
                          className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl transition-all flex items-center justify-center gap-2 font-medium text-sm shadow-md hover:shadow-lg"
                        >
                          <Receipt className="w-4 h-4" />
                          ดูใบเสร็จ
                        </button>
                        <button
                          onClick={() => toggleCardExpansion(purchase.id)}
                          className="flex-1 py-2 px-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors flex items-center justify-center gap-2 text-gray-700 font-medium text-sm"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              ซ่อนรายละเอียด
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              ดูรายละเอียดเพิ่มเติม
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 md:p-12">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <History className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">ยังไม่มีประวัติการซื้อ</h3>
                  <p className="text-gray-600">ประวัติการซื้อของคุณจะแสดงที่นี่</p>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="max-w-2xl">
            {/* User Info */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-gray-900">
                <User className="w-6 h-6 text-purple-600" />
                ข้อมูลส่วนตัว
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600 font-medium">อีเมล</p>
                    <p className="font-semibold text-gray-900 break-all">{userData?.email}</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <p className="text-sm text-blue-700 font-bold">Username</p>
                    </div>
                    {!editingUsername && (
                      <button
                        onClick={() => {
                          setEditingUsername(true);
                          setNewUsername(userData?.username || '');
                          setUsernameError('');
                          setUsernameSuccess('');
                        }}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all"
                      >
                        แก้ไข
                      </button>
                    )}
                  </div>
                  
                  {editingUsername ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value.toLowerCase())}
                        className="w-full px-4 py-2 bg-white border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-gray-900 font-mono"
                        placeholder="username"
                      />
                      {usernameError && (
                        <p className="text-sm text-red-600 font-medium">{usernameError}</p>
                      )}
                      {usernameSuccess && (
                        <p className="text-sm text-green-600 font-medium">{usernameSuccess}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={handleUpdateUsername}
                          disabled={savingUsername}
                          className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                        >
                          {savingUsername ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              กำลังบันทึก...
                            </>
                          ) : (
                            'บันทึก'
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingUsername(false);
                            setNewUsername('');
                            setUsernameError('');
                            setUsernameSuccess('');
                          }}
                          disabled={savingUsername}
                          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                        >
                          ยกเลิก
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="font-bold text-xl text-gray-900 font-mono">{userData?.username || '-'}</p>
                  )}
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">ชื่อ-นามสกุล</p>
                    <p className="font-semibold text-gray-900">{userData?.fullName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">เบอร์โทร</p>
                    <p className="font-semibold text-gray-900">{userData?.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">สมัครเมื่อ</p>
                    <p className="font-semibold text-gray-900">
                      {userData?.createdAt?.toDate?.()?.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' }) || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-gray-900">
                <Lock className="w-6 h-6 text-purple-600" />
                เปลี่ยนรหัสผ่าน
              </h3>

              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <p className="text-green-700 font-medium">{passwordSuccess}</p>
                </div>
              )}

              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                  <p className="text-red-700 font-medium">{passwordError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">รหัสผ่านปัจจุบัน</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-gray-900"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">รหัสผ่านใหม่</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-gray-900"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">ยืนยันรหัสผ่านใหม่</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-gray-900"
                    placeholder="••••••••"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      กำลังเปลี่ยน...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      เปลี่ยนรหัสผ่าน
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Link Google Account */}
            {userData?.provider === 'email' && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-gray-900">
                  <Chrome className="w-6 h-6 text-blue-600" />
                  เชื่อมต่อบัญชี Google
                </h3>
                <p className="text-gray-600 mb-4">
                  เชื่อมต่อบัญชี Google เพื่อให้คุณสามารถเข้าสู่ระบบด้วย Google ได้
                </p>
                <button
                  onClick={handleLinkGoogle}
                  className="w-full bg-white hover:bg-gray-50 text-gray-900 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-[1.02] border-2 border-gray-300 hover:border-blue-500 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                >
                  <Chrome className="w-5 h-5 text-blue-600" />
                  เชื่อมต่อบัญชี Google
                </button>
              </div>
            )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CoursededDashboard;
